import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Button, Input } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * RelatedParcelsPanel — the "Related Parcels" tab on a parcel's detail page.
 *
 * Reads GET /api/parcels/:id/related: the parcel's DERIVED siblings (other parcels
 * on the SAME property) PLUS any EXPLICIT relationships an operator created (RH§4).
 * Explicit rows carry `manual: true` + a `relationship_id`, so they can be removed;
 * derived siblings are read-only. "Add related parcel" opens a searchable picker
 * that POSTs a new explicit relationship.
 */

interface RelatedParcel {
  id: string;
  related_parcel_id: string;
  account_number: string;
  name: string | null;
  relationship: string | null;
  manual?: boolean;
  relationship_id?: string;
}

export interface RelatedParcelsPanelProps {
  parcelId: string;
}

export function RelatedParcelsPanel({ parcelId }: RelatedParcelsPanelProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const relatedQuery = useQuery<RelatedParcel[]>({
    queryKey: ['related-parcels', parcelId],
    queryFn: ({ signal }) => api.get<RelatedParcel[]>(`/api/parcels/${parcelId}/related`, signal),
  });

  const removeMutation = useMutation<unknown, Error, string>({
    mutationFn: (relatedId) => api.delete(`/api/parcels/${parcelId}/related/${relatedId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['related-parcels', parcelId] });
      toast({ variant: 'success', message: 'Relationship removed.' });
    },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not remove.' }),
  });

  if (relatedQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading related parcels…</p>;
  }
  if (relatedQuery.error) {
    return (
      <div style={{
        padding: 'var(--sp-5)', background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        Failed to load related parcels. {(relatedQuery.error as Error).message}
      </div>
    );
  }

  const rows = relatedQuery.data ?? [];

  return (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0, fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
        }}>Related parcels ({rows.length})</h3>
        <Button variant="primary" size="sm" onClick={() => setAddOpen(true)}>Add related parcel</Button>
      </div>

      {rows.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--color-muted)' }}>No related parcels yet. Use “Add related parcel” to link one.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {rows.map(r => (
            <li key={r.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)',
              padding: 'var(--sp-2) var(--sp-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)',
            }}>
              <button
                type="button"
                onClick={() => navigate(`/parcels/${r.related_parcel_id}`)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
              >
                <span style={{ color: 'var(--brand-primary)', fontWeight: 'var(--fw-medium)' }}>
                  <span style={{ fontFamily: 'var(--font-mono)' }}>#{r.account_number}</span>{r.name ? ` · ${r.name}` : ''}
                </span>
                {r.relationship && <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>{r.relationship}</span>}
              </button>
              {r.manual && (
                <button
                  type="button"
                  onClick={() => removeMutation.mutate(r.related_parcel_id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-status-red)', font: 'inherit' }}
                  title="Remove this relationship"
                >Remove</button>
              )}
            </li>
          ))}
        </ul>
      )}

      <AddRelatedParcelModal
        parcelId={parcelId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        existingIds={new Set(rows.map(r => r.related_parcel_id))}
      />
    </div>
  );
}

interface SearchResult { entity_type: string; id: string; label: string; sublabel: string | null }

// RH§4 — searchable picker to relate another parcel (cross-entity search filtered
// to parcels). Excludes the current parcel + any already-related ones.
function AddRelatedParcelModal({ parcelId, open, onClose, existingIds }: {
  parcelId: string; open: boolean; onClose: () => void; existingIds: Set<string>;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const [relType, setRelType] = useState('');

  const searchQuery = useQuery<{ results: SearchResult[] }>({
    queryKey: ['parcel-search', query],
    queryFn: ({ signal }) => api.get<{ results: SearchResult[] }>(`/api/search?q=${encodeURIComponent(query)}`, signal),
    enabled: open && query.trim().length >= 2,
  });
  const candidates = (searchQuery.data?.results ?? [])
    .filter(r => r.entity_type === 'parcel' && r.id !== parcelId && !existingIds.has(r.id));

  const addMutation = useMutation<unknown, Error, string>({
    mutationFn: (relatedId) => api.post(`/api/parcels/${parcelId}/related`, { related_parcel_id: relatedId, relationship_type: relType.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['related-parcels', parcelId] });
      setQuery(''); setRelType('');
      toast({ variant: 'success', message: 'Related parcel added.' });
      onClose();
    },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not add the relationship.' }),
  });

  return (
    <Modal open={open} onClose={onClose} title="Add a related parcel" size="lg">
      <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
        <label>
          <span style={{ display: 'block', marginBottom: 4, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Relationship (optional)</span>
          <Input placeholder="e.g. Adjacent lot, Same owner" value={relType} onChange={(e) => setRelType(e.target.value)} />
        </label>
        <label>
          <span style={{ display: 'block', marginBottom: 4, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Find a parcel</span>
          <Input placeholder="Search by account # or name…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)' }}>
          {query.trim().length < 2 ? (
            <p style={{ padding: 'var(--sp-3)', color: 'var(--color-ink-3)' }}>Type at least 2 characters to search.</p>
          ) : searchQuery.isLoading ? (
            <p style={{ padding: 'var(--sp-3)', color: 'var(--color-ink-3)' }}>Searching…</p>
          ) : candidates.length === 0 ? (
            <p style={{ padding: 'var(--sp-3)', color: 'var(--color-ink-3)' }}>No matching parcels.</p>
          ) : (
            candidates.map(c => (
              <button
                key={c.id}
                type="button"
                disabled={addMutation.isPending}
                onClick={() => addMutation.mutate(c.id)}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid var(--color-border)', padding: 'var(--sp-2) var(--sp-3)', cursor: 'pointer', color: 'var(--color-ink)', font: 'inherit' }}
              >
                <span style={{ fontWeight: 'var(--fw-medium)' }}>{c.label}</span>
                {c.sublabel && <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)' }}> · {c.sublabel}</span>}
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
